import React, { useState, Suspense } from 'react';
import { getTasks, getClasses, getCompletedTasks } from '../../service';
import ReactiveRender, { isTabletView } from '../ReactiveRender';
import TasksDesktop from '../TasksDesktop';
import { taskHeaderProps } from '../../utils/headerProps.js';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import Loader from '../Loader';
import { TaskListSkeleton } from '../Skeleton';
import { useQuery } from '@tanstack/react-query';
import {
  CalenderContainer,
  FilterAndSortContainer,
  MainContainer,
  TasksImg,
  TasksImgCal,
} from './style.js';
import RoundedDropDown from '../../components2/RoundedDropDown/index.jsx';
import TaskSelected from '../../static/img/Columns-new.svg';
import TaskNotSelected from '../../static/img/Columns-new-gray.svg';
import CalNotSelected from '../../static/img/Calendar-new-purple.svg';
import CalSelected from '../../static/img/Calendar-new.svg';
const MyCalendar = React.lazy(() => import('../../components2/Calender/index.js'));
import {
  FilterContainer,
  FilterLine,
  TitleHeading,
} from '../FilterSort/style.js';
import MenuButton from '../MenuButton/index.jsx';
import { useActiveClassData, useTasks } from '../state/hooks.js';
import SortItems from '../../components2/SortItems/index.jsx';
import {
  filterTasksByProgressAndClass,
  hasCurrentStudentTasks,
} from '../../service/tasks.js';
export default function StudentTaskRoot() {
  const [allTasks, setAllTasks] = React.useState([]);
  const [classes, setClasses] = React.useState([]);
  const [filteredTasks, setFilteredTasks] = React.useState([]);
  const [sortItem, setSortItems] = React.useState(true);
  const [selectedSubject, setSelectedSubject] = React.useState('');
  const [tasksSelected, setTasksSelected] = React.useState(true);
  const [isShowMenu, setShowMenu] = React.useState(false);
  const { data: classData, isLoadingdata: isLoadingclassData } = useActiveClassData();
  const [visibleInProgressTaskCount, setVisibleInProgressTaskCount] =
    useState(3);
  const [visibleAssignedTaskCount, setvisibleAssignedTaskCount] = useState(3);
  const [visibleInReviewTaskCount, setvisibleInReviewTaskCount] = useState(3);
  const tabletView = isTabletView();


  const {
    data: tasks,
    isLoadingdata: isLoadingTasks
  } = useTasks();

  const studentClassesQuery = useQuery({
    queryKey: ['classes'],
    queryFn: async () => {
      const result = await getClasses();
      return result;
    },
    staleTime: 300_000,
  });
  // Same query as the sidebar's Closed Tasks count, so it is fetched once.
  const completedTasksQuery = useQuery({
    queryKey: ['completedTasks'],
    queryFn: async () => {
      const result = await getCompletedTasks();
      return result;
    },
  });

  React.useEffect(() => {
    if (tasks) {
      setFilteredTasks(tasks);
      setAllTasks(tasks);
    }
    if (studentClassesQuery.data) {
      setClasses(studentClassesQuery.data);
    }
  }, [tasks, studentClassesQuery.data]);

  const setSelectedValue = (type, selectValue) => {
    if (type === 'subjects') {
      setSelectedSubject(selectValue);
    }
  };

  if (
    isLoadingTasks ||
    studentClassesQuery.isLoading ||
    isLoadingclassData
  ) {
    return <TaskListSkeleton />;
  }

  const filteredData = (tasks) => {
    const sortedTasks = tasks.sort((a, b) => {
      const dateA = new Date(a.dueAt).getTime();
      const dateB = new Date(b.dueAt).getTime();
      return sortItem ? dateB - dateA : dateA - dateB;
    });

    return sortedTasks;
  };

  // Build subject options — show code in brackets only when names are duplicated
  const subjectEntries = [];
  const seenCodes = new Set();
  filteredTasks.forEach((task) => {
    const subj = task.subjects?.[0];
    const name = task.subjectName || (typeof subj === 'object' ? subj?.subjectName : subj) || '';
    const code = task.subjectCode || (typeof subj === 'object' ? subj?.subjectCode || subj?.code : '') || '';
    if (!name) return;
    const key = code || name;
    if (!seenCodes.has(key)) {
      seenCodes.add(key);
      subjectEntries.push({ name, code });
    }
  });
  const nameCount = {};
  subjectEntries.forEach(({ name }) => { nameCount[name] = (nameCount[name] || 0) + 1; });
  const subjectDisplayMap = {};
  const subjectNames = subjectEntries.map(({ name, code }) => {
    const isDuplicate = nameCount[name] > 1 && code && code !== name;
    const label = isDuplicate ? `${name} (${code})` : name;
    subjectDisplayMap[label] = { name, code, isDuplicate };
    return label;
  });

  const matchesSelectedSubject = (task) => {
    if (!selectedSubject) return true;
    const mapped = subjectDisplayMap[selectedSubject];
    if (!mapped) return true;
    const subj = task.subjects?.[0];
    if (mapped.isDuplicate && mapped.code) {
      const taskCode = task.subjectCode || (typeof subj === 'object' ? subj?.subjectCode || subj?.code : '');
      return taskCode === mapped.code;
    }
    const taskName = task.subjectName || (typeof subj === 'object' ? subj?.subjectName : subj) || '';
    return taskName === mapped.name;
  };

  const assignedTasks = filterTasksByProgressAndClass(
    filteredData(filteredTasks),
    'ASSIGNED'
  ).filter(matchesSelectedSubject);
  const inProgressTasks = filterTasksByProgressAndClass(
    filteredData(filteredTasks),
    'DRAFT'
  ).filter(matchesSelectedSubject);
  const inReviewTasks = filterTasksByProgressAndClass(
    filteredData(filteredTasks),
    'REVIEW'
  ).filter(matchesSelectedSubject);

  const visibleInProgressTasks = inProgressTasks.slice(
    0,
    visibleInProgressTaskCount
  );
  const visibleAssignedTasks = assignedTasks.slice(0, visibleAssignedTaskCount);
  const visibleInReviewTasks = inReviewTasks.slice(0, visibleInReviewTaskCount);

  const classesItems = classes.map((clazz) => {
    return { value: clazz.id, label: clazz.title, category: 'CLASSES' };
  });

  const menuItems = [
    {
      name: 'TYPES',
      title: 'Types',
      items: [
        { value: 'ASSIGNMENT', label: 'Submissions', category: 'TYPES' },
        { value: 'REVIEW', label: 'Reviews', category: 'TYPES' },
        {
          value: 'DRAFT_REVIEW',
          label: 'Community',
          category: 'TYPES',
        },
      ],
    },
    {
      name: 'CLASSES',
      title: 'Classes',
      items: classesItems,
    },
  ];

  const filterTasks = (selectedItems) => {
    const groupedData = groupBy(selectedItems, 'category');
    let typesValues = (get(groupedData, 'TYPES') || []).map((item) => item.value);
    if (typesValues.length === 0) {
      typesValues = ['REVIEW', 'ASSIGNMENT', 'DRAFT_REVIEW'];
    }
    const filteredTasks = allTasks.filter((task) =>
      typesValues.includes(task.type)
    );

    let classesValues = (get(groupedData, 'CLASSES') || []).map((item) => item.value);
    if (classesValues.length === 0) {
      classesValues = classes.map((clazz) => clazz.id);
    }
    classesValues = [null, undefined, ...classesValues];
    const filteredClasses = filteredTasks.filter((task) =>
      classesValues.includes(task.classId)
    );

    setFilteredTasks(filteredClasses);
  };

  const handleShowMoreTask = (type, numberOfTasks) => {
    if (type === 'inDraft') {
      setVisibleInProgressTaskCount(numberOfTasks);
    }
    if (type === 'Assigned') {
      setvisibleAssignedTaskCount(numberOfTasks);
    }
    if (type === 'inReview') {
      setvisibleInReviewTaskCount(numberOfTasks);
    }
  };

  const FilterSortAndCal = (
    <>
      <MainContainer>
        <CalenderContainer>
          {tabletView && <MenuButton setShowMenu={setShowMenu} />}
          <TitleHeading
            style={tasksSelected ? { color: '#7200E0' } : { color: '#7B7382' }}
            className={tasksSelected ? 'active' : ''}
            onClick={() => setTasksSelected(true)}
          >
            <TasksImg
              src={!tasksSelected ? TaskNotSelected : TaskSelected}
              selected={tasksSelected}
            />
            <span data-dd-privacy="mask-user-input">Column</span>
          </TitleHeading>

          <TitleHeading
            style={tasksSelected ? { color: '#7B7382' } : { color: '#7200E0' }}
            className={!tasksSelected ? 'active' : ''}
            onClick={() => setTasksSelected(false)}
          >
            <TasksImgCal
              src={!tasksSelected ? CalNotSelected : CalSelected}
              selected={!tasksSelected}
            />
            <span data-dd-privacy="mask-user-input">Calendar</span>
          </TitleHeading>
        </CalenderContainer>
        <FilterAndSortContainer>
          <FilterContainer>
            <RoundedDropDown
              search={false}
              type={'subjects'}
              selectedIndex={setSelectedValue}
              menuItems={subjectNames}
              defaultValue={selectedSubject}
              emptyLabel="All Subjects"
              width={110}
            />
          </FilterContainer>
          {!isTabletView && <FilterLine />}
          {tasksSelected && (
            <SortItems
              sortItem={sortItem}
              setSortItem={setSortItems}
              firstSortText={'New to Old'}
              secondSortText={'Old to New'}
            />
          )}
        </FilterAndSortContainer>
      </MainContainer>
    </>
  );

  // Skip tasks without due date — Date(undefined) is Invalid Date and
  // react-big-calendar can't place them. Undated tasks still appear in the list view.
  const calenderEvents = allTasks
    .filter(matchesSelectedSubject)
    .filter((task) => task.dueAt)
    .map((task) => ({
      link: task.link,
      title: task.title,
      class: task.classTitle,
      start: new Date(task.dueAt),
      end: new Date(task.dueAt),
    }));

  const MyCalendarFile = (
    <Suspense fallback={<TaskListSkeleton items={3} />}>
      <MyCalendar calenderEvents={calenderEvents} />
    </Suspense>
  );

  // The empty state (JED-1272) is about the student's tasks, not the subject
  // filter, so it reads the unfiltered tasks. It waits for the closed tasks so
  // its message is right first time; "none assigned yet" needs them loaded.
  const hasNoCurrentTasks =
    Array.isArray(tasks) &&
    !hasCurrentStudentTasks(tasks) &&
    !completedTasksQuery.isLoading;
  const hasClosedTasks = !(
    Array.isArray(completedTasksQuery.data) &&
    completedTasksQuery.data.length === 0
  );

  return (
    <TasksDesktop
      {...{
        visibleAssignedTasks,
        visibleInProgressTasks,
        visibleInReviewTasks,
        visibleInProgressTaskCount,
        visibleAssignedTaskCount,
        visibleInReviewTaskCount,
        inProgressTasks,
        assignedTasks,
        inReviewTasks,
        FilterSortAndCal,
        tasksSelected,
        MyCalendarFile,
        handleShowMoreTask,
        isShowMenu,
        setShowMenu,
        ...tasksDesktopData(classData),
        hasNoCurrentTasks,
        hasClosedTasks,
      }}
    />
  );
}

const statusBubbles28Data = {
  children: 'Theory',
};
const statusBubbles32Data = {
  star1: '/img/star1@2x.png',
};

const frame66Data = {
  statusBubbles2Props: statusBubbles28Data,
  statusBubbles3Props: statusBubbles32Data,
};

const content6Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards6Data = {
  frame6Props: frame66Data,
  contentProps: content6Data,
};

const statusBubbles29Data = {
  children: 'Assignment',
};

const statusBubbles210Data = {
  children: 'MCQ',
};

const frame622Data = {
  statusBubbles21Props: statusBubbles29Data,
  statusBubbles22Props: statusBubbles210Data,
};

const content7Data = {
  dueOn2April2023: 'Due on 10 April 2023',
};

const cards22Data = {
  frame62Props: frame622Data,
  contentProps: content7Data,
};

const statusBubbles211Data = {
  children: 'MCQ',
};

const frame632Data = {
  statusBubbles2Props: statusBubbles211Data,
};

const content8Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards32Data = {
  frame63Props: frame632Data,
  contentProps: content8Data,
};

const statusBubbles212Data = {
  children: 'Theory',
};

const frame642Data = {
  statusBubbles2Props: statusBubbles212Data,
};

const content9Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards42Data = {
  frame64Props: frame642Data,
  contentProps: content9Data,
};

const statusBubbles213Data = {
  children: 'Assignment',
};

const statusBubbles214Data = {
  children: 'Theory',
};

const frame652Data = {
  statusBubbles21Props: statusBubbles213Data,
  statusBubbles22Props: statusBubbles214Data,
};

const content10Data = {
  dueOn2April2023: 'Due on 2 April 2023',
};

const cards52Data = {
  frame65Props: frame652Data,
  contentProps: content10Data,
};

const frame192Data = {
  className: 'frame-19-1',
  cardsProps: cards6Data,
  cards2Props: cards22Data,
  cards3Props: cards32Data,
  cards4Props: cards42Data,
  cards5Props: cards52Data,
};

const frame13043Data = {
  iconsaxLinearSort: '/img/iconsax-linear-sort-2@2x.png',
};
const frame13061Data = {
  frame1304Props: frame13043Data,
};
const tasksDesktopData = (classData) => {
  return {
    title: 'Tasks',
    frame1306Props: frame13061Data,
    frame19Props: frame192Data,
    headerProps: taskHeaderProps(classData),
    arrowright: '/img/arrowright@2x.png',
  };
};
